import { FC, Fragment } from 'react';
import type { Editor } from '@tiptap/react';
import { Dropdown, Menu } from 'antd';
import { capitalCase } from 'change-case';
import type { IconType } from 'react-icons';
import { TbBallpen, TbBlockquote, TbH1, TbH2, TbH3, TbH4, TbH5, TbH6 } from 'react-icons/tb';

import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { DropdownMenuLabel, DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';

export interface RichTextFormatDropdownProps {
    disabled?: boolean;
    editor: Editor;
}

interface RichTextFormat {
    action: () => void;
    icon: IconType;
    key: string;
    label: string;
}

const getCurrentBlockType = (editor: Editor) => {
    if (editor.isActive('heading')) {
        return `heading-${editor.getAttributes('heading').level}`;
    }

    if (editor.isActive('paragraph')) {
        return 'paragraph';
    }

    if (editor.isActive('blockquote')) {
        return 'blockquote';
    }

    return 'paragraph';
};

const RichTextFormatDropdown: FC<RichTextFormatDropdownProps> = ({ disabled, editor }): JSX.Element => {
    const currentBlockType = getCurrentBlockType(editor);
    const formats: RichTextFormat[] = [
        {
            action: () => editor.chain().focus().setParagraph().run(),
            icon: TbBallpen,
            key: 'paragraph',
            label: 'Paragraph',
        },
        ...([1, 2, 3, 4, 5, 6] as const).map((level) => ({
            action: () => editor.chain().focus().toggleHeading({ level }).run(),
            icon: [TbH1, TbH2, TbH3, TbH4, TbH5, TbH6][level - 1],
            key: `heading-${level}`,
            label: `Heading ${level}`,
        })),
        {
            action: () => editor.chain().focus().toggleBlockquote().run(),
            icon: TbBlockquote,
            key: 'blockquote',
            label: 'Blockquote',
        },
    ];

    return (
        <Dropdown
            disabled={disabled}
            popupRender={() => (
                <DropdownMenuPanel
                    panelStyle={{ marginTop: '0.5rem' }}
                    selectedKeys={[currentBlockType]}
                    style={{ width: '16rem' }}
                >
                    <DropdownMenuLabel>Change Active Text Format</DropdownMenuLabel>
                    <Menu.Divider />
                    {formats.map((format, index) => {
                        const Icon = format.icon;

                        return (
                            <Fragment key={format.key}>
                                {index === 1 || index === formats.length - 1 ? <Menu.Divider /> : null}
                                <Menu.Item icon={<Icon size='1rem' />} key={format.key} onClick={format.action}>
                                    {format.label}
                                </Menu.Item>
                            </Fragment>
                        );
                    })}
                </DropdownMenuPanel>
            )}
            placement='bottomLeft'
            trigger={['click']}
        >
            <DropdownButton disabled={disabled} size='small'>
                <span style={{ paddingRight: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>
                    {capitalCase(currentBlockType)}
                </span>
            </DropdownButton>
        </Dropdown>
    );
};

export default RichTextFormatDropdown;
